import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { IEntityWithLabelLocations } from "@common/ADAPT.Common.Model/organisation/entity-with-label-locations";
import { Item } from "@common/ADAPT.Common.Model/organisation/item";
import { Label } from "@common/ADAPT.Common.Model/organisation/label";
import { LabelLocation } from "@common/ADAPT.Common.Model/organisation/label-location";
import { Objective } from "@common/ADAPT.Common.Model/organisation/objective";
import { ProcessStep } from "@common/ADAPT.Common.Model/organisation/process-step";
import { Role } from "@common/ADAPT.Common.Model/organisation/role";
import { SystemEntity } from "@common/ADAPT.Common.Model/organisation/system-entity";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import isEqual from "lodash.isequal";
import { forkJoin, merge, Observable, of, timer } from "rxjs";
import { filter, map, startWith, switchMap, tap } from "rxjs/operators";
import { LabellingService } from "../labelling.service";

@Component({
    selector: "adapt-select-label-for-related-entity",
    templateUrl: "./select-label-for-related-entity.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class SelectLabelForRelatedEntityComponent extends BaseComponent implements OnInit {
    // only set one of these input
    @Input() public set item(value: Item) {
        this.setEntity(value);
    }

    @Input() public set team(value: Team) {
        this.setEntity(value);
    }

    @Input() public set objective(value: Objective) {
        this.setEntity(value);
    }

    @Input() public set system(value: SystemEntity) {
        this.setEntity(value);
    }

    @Input() public set processStep(value: ProcessStep) {
        this.setEntity(value);
    }

    @Input() public set role(value: Role) {
        this.setEntity(value);
    }

    @Output() public entitiesChange = new EventEmitter<LabelLocation[]>();

    public labels$: Observable<Label[]> = of([]);
    private relatedEntity?: IEntityWithLabelLocations;
    private removedLabelLocations: LabelLocation[] = [];

    public constructor(
        private labellingService: LabellingService,
        private rxjsBreezeService: RxjsBreezeService,
    ) {
        super();
    }

    public ngOnInit() {
        if (!this.relatedEntity) {
            throw new Error("At least one of related entity is expected to be set");
        }

        this.labels$ = this.labellingService.getLabelsForEntity(this.relatedEntity).pipe(
            // The following emit to handle update of label when label location deleted / add cancelled
            switchMap(() => merge(
                this.rxjsBreezeService.entityTypeChanged(LabelLocation),
                this.rxjsBreezeService.entityTypeChanged(Label).pipe(
                    // note that cannot use entityTypeDetached here as we want to pick up changes from remote
                    filter((label: Label) => label.entityAspect.entityState.isDetached()),
                    startWith(undefined),
                ))),
            map(() => this.relatedEntity!.labelLocations),
            map((labelLocations) => labelLocations.map((i) => i.label)),
        );
    }

    public onLabelsChanged(labels: Label[], existingLabels: Label[]) {
        if (isEqual(labels, existingLabels)) {
            // no changes to labels, don't emit the label change
            return;
        }

        const removeEntities: LabelLocation[] = [];
        this.relatedEntity!.labelLocations.forEach((existingLocation) => {
            if (!labels.find((i) => i.labelId === existingLocation.labelId)) {
                // label removed from select-label
                removeEntities.push(existingLocation);
            }
        });

        const addLabels = labels
            .filter((i) => !this.relatedEntity!.labelLocations.find((existingLocation) => existingLocation.labelId === i.labelId))
            .map((label) => this.labellingService.createLabelLocationFromLabelForEntity(label, this.relatedEntity));

        const removeObservables = removeEntities.map((i) => this.labellingService.remove(i));
        // last emit for the case where there is no remove observables
        removeObservables.push(of(undefined));
        forkJoin(removeObservables).pipe(
            tap(() => this.removedLabelLocations = this.removedLabelLocations.concat(removeEntities)),
            switchMap(() => addLabels.length > 0 ? forkJoin(addLabels) : of(undefined)),
            tap((newLabelLocations) => {
                if (newLabelLocations?.length) {
                    // newly created label locations -> reorder labelLocationId so that latest created will be largest
                    // otherwise server will return ids that's the other way round, e.g.
                    // new label location with id of -3, -4, will get LabelLocationId of 26 (from -3), 25 (from -4) as -4 is smaller than -3
                    const newLocations = this.relatedEntity!.labelLocations.filter((i) => i.entityAspect.entityState.isAdded());
                    const newLocationIds = newLocations.map((i) => i.labelLocationId).sort((a, b) => a - b);
                    let smallestId = newLocationIds[0];
                    for (let i = 0; i < newLocations.length; i++) {
                        // breeze won't allow id key that's already in use
                        const conflictLocation = newLocations.find((l) => l.labelLocationId === newLocationIds[i]);
                        if (conflictLocation) {
                            // this will get reassigned later - just move it away to some unused id to free up the id
                            // to be used by this current label location
                            conflictLocation.labelLocationId = --smallestId;
                        }

                        newLocations[i].labelLocationId = newLocationIds[i];
                    }
                }
            }),
            switchMap(() => timer(0)), // emit next digest cycle to prevent ExpressionChanged error in subscription to entitiesChange, e.g. in edit obj dialog
            this.takeUntilDestroyed(),
        ).subscribe(() => this.entitiesChange.emit([...this.removedLabelLocations, ...this.relatedEntity!.labelLocations]));
    }

    private setEntity(entity: IEntityWithLabelLocations) {
        if (this.relatedEntity) {
            throw new Error("Exactly 1 related entity is expected through @Input()");
        }

        this.relatedEntity = entity;
    }
}
